/* eslint-disable react-hooks/exhaustive-deps */
import { useHistory, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import InsightsCards from '../insights-cards/insights-cards.component';
import {
  ANALYTICS_EVENTS,
  COMPONENT_NAME,
  ELEMENT_TYPE,
  EVENT_GROUP,
  PRODUCT_AREA,
  UPGRADE_TO_PRO,
} from '../../../../../consts/analytics';
import { useAnalyticsContext } from '../../../../../contexts/analytics/useAnalyticsContext';
import { THEME } from '../../../../../themes';
import { PaymentSuccessDialogReferrer } from '../../../../../components/dialogs/payment-success-dialog/payment-success-dialog.enums';
import { AppRouting } from '../../../../../services/appRoutingResolver';
import UpgradeToProDialog
  from '../../../../../components/dialogs/time-to-pay-dialogs/upgrade-to-pro-dialog/upgrade-to-pro-dialog.component';
import MatchLimitDailyBase from '../match-daily-limit-base/match-daily-limit.component';
import { PricingPlanEnum } from '../../../../../enums/user-plan.enum';
import STRING_KEYS from '../../../../../language/keys';
import EngageOrDeclineCard from '../engage-or-decline-card/engage-or-decline-card.component';
import { GhostButton } from '../../../../../components/Common/Buttons/ghost-button/ghost-button.component';
import { FilledButton } from '../../../../../components/Common/Buttons/filled-button/filled-button.component';
import { CREATE_POSITION_ANALYTICS_EVENTS } from '../../../../create-position/consts/position-analytics.consts';
import { PAGE_NAMES } from '../../../../../consts/pages';
import { useGetMyAccountSelector } from '../../../../../store/rtk-query/apis/account/selectors/get-account-by-id.selector';
import { getPendingReviewMatchesQuery } from '../../../../../store/rtk-query/apis/match/hooks/get-pending-review-matches.query-hook';
import { LianaIcon, SlothIcon } from './match-daily-limit-free.styled';

const MatchLimitDailyFree = ():JSX.Element | null => {
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const { positionId } = useParams<MatchUrlParams>();
  const browserHistory = useHistory();
  const { t: translate } = useTranslation();
  const { data: { plan } } = useGetMyAccountSelector();
  const { analyticsTrackEvent } = useAnalyticsContext();

  const { data: pendingReviewMatches } = getPendingReviewMatchesQuery(positionId);

  useEffect(() => {
    if (isUpgradeDialogOpen) {
      const eventData = {
        eventName: CREATE_POSITION_ANALYTICS_EVENTS.NEW_POSITION_PREMIUM_MODAL_SHOWN,
        eventGroup: EVENT_GROUP.IMP,
        pageName: PRODUCT_AREA.MATCH_PAGE,
        productArea: `${PRODUCT_AREA.MATCH_PAGE}`,
        componentName: plan?.pricingPlan !== PricingPlanEnum.LITE ? COMPONENT_NAME.ADD_POSITION_PRO : COMPONENT_NAME.ADD_POSITION_LITE,
        pagePositionId: positionId,
      };

      analyticsTrackEvent(eventData);
    }
  }, [isUpgradeDialogOpen]);

  const onClickUpgrade = () => {
    const eventData = {
      eventName: ANALYTICS_EVENTS.CLICKED_ON_GET_UNLIMITED_MATCHES,
      eventGroup: EVENT_GROUP.CLICK,
      pageName: PAGE_NAMES.MATCH_LIMIT,
      productArea: PRODUCT_AREA.MATCH_PAGE,
      pagePositionId: positionId,
    };

    analyticsTrackEvent(eventData);
    setIsUpgradeDialogOpen(true);
  };

  const shouldReviewMatches = pendingReviewMatches?.length;

  const onClickGoToPositionReachout = () => {
    const eventData = {
      eventName: ANALYTICS_EVENTS.CLICKED_ON_REACH_YOUR_MATCHES,
      eventGroup: EVENT_GROUP.CLICK,
      pageName: PAGE_NAMES.MATCH_LIMIT,
      productArea: PRODUCT_AREA.MATCH_PAGE,
      pagePositionId: positionId,
    };

    analyticsTrackEvent(eventData);
    browserHistory.push(
      AppRouting.buildPositionConversationsPageUrl(positionId),
    );
  };

  const onDialogClickBIEvent = (type: string) => {
    const clickOnDialogEventData = {
      eventName: ANALYTICS_EVENTS.CLICKED_ON_PREMIUM_MODAL,
      eventGroup: EVENT_GROUP.CLICK,
      productArea: PRODUCT_AREA.MATCH_PAGE,
      pageName: PAGE_NAMES.MATCH_LIMIT,
      componentElementType: type,
      componentName: plan?.pricingPlan !== PricingPlanEnum.LITE ? COMPONENT_NAME.PRO_POSITION_LIMIT : COMPONENT_NAME.LITE_POSITION_LIMIT,
    };

    analyticsTrackEvent(clickOnDialogEventData);
  };

  const onClickUpgradeToPro = () => {
    const eventData = {
      eventName: ANALYTICS_EVENTS.CLICKED_ON_GO_PRO,
      eventGroup: EVENT_GROUP.CLICK,
      productArea: `${PRODUCT_AREA.MATCH_PAGE}`,
      pagePositionId: positionId,
      referral: UPGRADE_TO_PRO.MODAL_GO_PRO_MATCHES_LIMIT,
    };

    onDialogClickBIEvent(ELEMENT_TYPE.GO_PRO);
    analyticsTrackEvent(eventData);
  };

  const onDialogCloseHandler = () => {
    setIsUpgradeDialogOpen(false);
    onDialogClickBIEvent(ELEMENT_TYPE.CLOSE);
  };

  const description = useMemo(() => {
    if (shouldReviewMatches) {
      return translate(STRING_KEYS.DAILY_LIMIT.FREE_USER.PENDING_REVIEW.DESCRIPTION);
    }

    return translate(STRING_KEYS.DAILY_LIMIT.FREE_USER.MISSING_PENDING_REVIEW.DESCRIPTION);
  }, [shouldReviewMatches]);

  const GoToMatchesButton = useMemo(() => {
    if (!shouldReviewMatches) {
      return (
        <GhostButton
          buttonText={ translate(STRING_KEYS.DAILY_LIMIT.FREE_USER.MISSING_PENDING_REVIEW.GO_TO_MATCHES_BUTTON) }
          onClick={ onClickGoToPositionReachout }
          style={ {
            textColor: THEME.COLORS.SECONDARY,
            uppercase: true,
          } }
        />
      );
    }

    return undefined;
  }, [shouldReviewMatches]);

  const InsightsElement = useMemo(() => {
    if (shouldReviewMatches) {
      const icons: UserProfileImage[] = pendingReviewMatches?.map((match) => ({
        profileImage: match.talent?.profileImage,
        userName: match.talent?.firstName,
      }));

      return (
        <EngageOrDeclineCard icons={ icons } />
      );
    }

    return (
      <InsightsCards
        positionId={ positionId }
      />
    );
  }, [shouldReviewMatches]);

  const UpgradeToProButton: JSX.Element = (
    <FilledButton
      buttonText={ translate(STRING_KEYS.DAILY_LIMIT.FREE_USER.UPGRADE_TO_PRO_BUTTON) }
      onClick={ onClickUpgrade }
      style={ {
        backgroundColor: THEME.COLORS.SECONDARY,
        activeBackgroundColor: THEME.COLORS_RGB.SECONDARY_024,
        hoveredBackgroundColor: THEME.COLORS_RGB.SECONDARY_064,
        uppercase: true,
      } }
    />
  );

  return (
    <>
      <MatchLimitDailyBase
        Background={ <LianaIcon /> }
        Icon={ <SlothIcon /> }
        title={ translate(STRING_KEYS.DAILY_LIMIT.FREE_USER.TITLE) }
        description={ description }
        FilledButton={ UpgradeToProButton }
        GhostButton={ GoToMatchesButton }
        InsightsElement={ InsightsElement }
      />
      { isUpgradeDialogOpen && (
        <UpgradeToProDialog
          title={ translate(STRING_KEYS.UPGRADE_TO_PRO_DIALOG.UPGRADE_TO_PRO_TITLE) }
          subTitle={ translate(STRING_KEYS.UPGRADE_TO_PRO_DIALOG.UPGRADE_TO_PRO_SUB_TITLE) }
          isOpen={ isUpgradeDialogOpen }
          onClose={ onDialogCloseHandler }
          onActionClick={ onClickUpgradeToPro }
          referrer={ PaymentSuccessDialogReferrer.MatchDailyStatistics }
        />
      )}
    </>
  );
};

export default MatchLimitDailyFree;
